import { useCallback, useEffect, useState } from "react";
import {
  DndContext, DragOverlay, pointerWithin,
  type DragStartEvent, type DragEndEvent,
} from "@dnd-kit/core";
import { useDndSensors } from "@/lib/dnd-sensors";
import type {
  AgeGroupPublic,
  TeamPublic,
  TeamPlayerPublic,
  TeamPositionEntryPublic,
  PoolPlayerPublic,
  CoachTeamPublic,
  CoachPublic,
  TeamPlayerRole,
} from "@dugads/shared";
import { getPositionsForSport, isReservePosition } from "@dugads/shared";
import * as api from "@/lib/api";
import { PlayerPoolSidebar } from "./player-pool-sidebar";
import { TeamTabBar } from "./team-tab-bar";
import { TeamRosterPanel } from "./team-roster-panel";

interface InlineTeamBuilderProps {
  ageGroup: AgeGroupPublic;
  schoolId: string;
}

export function InlineTeamBuilder({ ageGroup, schoolId }: InlineTeamBuilderProps) {
  const [pool, setPool] = useState<PoolPlayerPublic[]>([]);
  const [teams, setTeams] = useState<TeamPublic[]>([]);
  const [rosters, setRosters] = useState<Map<string, TeamPlayerPublic[]>>(new Map());
  const [positions, setPositions] = useState<Map<string, TeamPositionEntryPublic[]>>(new Map());
  const [coaches, setCoaches] = useState<Map<string, CoachTeamPublic[]>>(new Map());
  const [schoolCoaches, setSchoolCoaches] = useState<CoachPublic[]>([]);
  const [activeTeamId, setActiveTeamId] = useState<string | null>(null);
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
  const [selectedPlayerIds, setSelectedPlayerIds] = useState<Set<string>>(new Set());
  const [highlightedPlayerId, setHighlightedPlayerId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [positionsLoading, setPositionsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Age groups are read-only lookup data; team editing is always allowed if user can view
  const canEdit = ageGroup._actions.includes("view");

  // Load pool + teams on mount
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      api.getAgeGroupPlayers(ageGroup.id, schoolId),
      api.getTeams(schoolId, ageGroup.id),
    ])
      .then(([poolRes, teamsRes]) => {
        if (cancelled) return;
        setPool(poolRes.players);
        setTeams(teamsRes.teams);
        const first = teamsRes.teams.find((t) => t.team_type === "main") ?? teamsRes.teams[0];
        if (first) setActiveTeamId(first.id);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof api.ApiError ? e.message : "Failed to load data");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    // Fetch school coaches separately (non-blocking -- don't fail page if no permission)
    api.getCoaches(schoolId)
      .then((res) => { if (!cancelled) setSchoolCoaches(res.coaches); })
      .catch(() => {}); // Silently ignore -- dropdown just won't show

    return () => { cancelled = true; };
  }, [ageGroup.id, schoolId]);

  // Load roster + positions when active team changes
  useEffect(() => {
    if (!activeTeamId) return;

    // Load roster if not cached
    if (!rosters.has(activeTeamId)) {
      setRosterLoading(true);
      api
        .getTeamPlayers(activeTeamId)
        .then((res) => {
          setRosters((prev) => new Map(prev).set(activeTeamId, res.players));
        })
        .catch(() => {
          setRosters((prev) => new Map(prev).set(activeTeamId, []));
        })
        .finally(() => setRosterLoading(false));
    }

    // Load positions if not cached
    if (!positions.has(activeTeamId)) {
      setPositionsLoading(true);
      api
        .getTeamPositions(activeTeamId)
        .then((res) => {
          setPositions((prev) => new Map(prev).set(activeTeamId, res.positions));
        })
        .catch(() => {
          setPositions((prev) => new Map(prev).set(activeTeamId, []));
        })
        .finally(() => setPositionsLoading(false));
    }

    // Load coaches if not cached
    if (!coaches.has(activeTeamId)) {
      api
        .getTeamCoaches(activeTeamId)
        .then((res) => {
          setCoaches((prev) => new Map(prev).set(activeTeamId, res.coaches));
        })
        .catch(() => {
          setCoaches((prev) => new Map(prev).set(activeTeamId, []));
        });
    }
  }, [activeTeamId, rosters, positions, coaches]);

  // Clear selections when switching teams
  useEffect(() => {
    setSelectedPlayerId(null);
    setHighlightedPlayerId(null);
    setSelectedPlayerIds(new Set());
  }, [activeTeamId]);

  // Build assigned map: player_id → assignments across all loaded rosters
  // A player can appear on multiple teams if they are a reserve on higher-ranked teams
  const assignedMap = new Map<string, { teamName: string; teamId: string; jerseyNumber?: number }[]>();
  for (const team of teams) {
    const roster = rosters.get(team.id);
    const teamPositions = positions.get(team.id);
    if (roster) {
      for (const tp of roster) {
        const posEntry = teamPositions?.find((p) => p.player_id === tp.player_id);
        const existing = assignedMap.get(tp.player_id) ?? [];
        existing.push({
          teamName: team.name,
          teamId: team.id,
          jerseyNumber: posEntry?.jersey_number,
        });
        assignedMap.set(tp.player_id, existing);
      }
    }
  }

  const activeTeam = teams.find((t) => t.id === activeTeamId) ?? null;
  const activeRoster = activeTeamId ? rosters.get(activeTeamId) ?? [] : [];
  const activePositions = activeTeamId ? positions.get(activeTeamId) ?? [] : [];
  const activeCoaches = activeTeamId ? coaches.get(activeTeamId) ?? [] : [];

  // Determine sport positions for reserve checking
  const activeSportPositions = activeTeam?.sport_name ? getPositionsForSport(activeTeam.sport_name) : undefined;

  /**
   * Check if a player is available for the active team.
   * A player is available if:
   * - They are not assigned to any team, OR
   * - They are ONLY assigned as reserves on alphabetically-higher teams than the active team
   * Once assigned as a starter on ANY team, they are unavailable for other teams.
   * If already on the active team, they are unavailable (already there).
   */
  function isPlayerAvailableForActiveTeam(playerId: string): boolean {
    const assignments = assignedMap.get(playerId);
    if (!assignments || assignments.length === 0) return true;
    if (!activeTeam) return false;

    for (const a of assignments) {
      // Already on the active team -- not available
      if (a.teamId === activeTeamId) return false;
      // If a starter (jersey 1-15) on ANY team -- not available
      if (a.jerseyNumber && activeSportPositions && !isReservePosition(activeSportPositions, a.jerseyNumber)) {
        return false;
      }
      // If on roster but no position yet -- treat as unavailable (not a confirmed reserve)
      if (a.jerseyNumber === undefined) return false;
      // If reserve on a lower-ranked team -- not available for this (higher) team
      if (a.teamName.localeCompare(activeTeam.name) > 0) return false;
    }
    // All assignments are reserves on higher-ranked teams -- available
    return true;
  }

  // Assign player to team roster + position in one flow
  const assignPlayerToPosition = useCallback(
    async (playerId: string, jerseyNumber: number, targetTeamId?: string) => {
      const teamId = targetTeamId ?? activeTeamId;
      if (!teamId || saving) return;

      setSaving(true);
      setError(null);
      try {
        // Check if player is available for this team (reserves on higher teams are allowed)
        if (!isPlayerAvailableForActiveTeam(playerId)) {
          setError("Player is already assigned to a team");
          return;
        }

        // Displace existing holder of this position (remove from roster + position)
        const currentPositions = positions.get(teamId) ?? [];
        const displaced = currentPositions.find((p) => p.jersey_number === jerseyNumber);
        if (displaced) {
          await api.deletePlayerPosition(teamId, displaced.player_id);
          await api.removeTeamPlayer(teamId, displaced.player_id);
          setPositions((prev) => {
            const next = new Map(prev);
            const existing = next.get(teamId) ?? [];
            next.set(teamId, existing.filter((p) => p.player_id !== displaced.player_id));
            return next;
          });
          setRosters((prev) => {
            const next = new Map(prev);
            const existing = next.get(teamId) ?? [];
            next.set(teamId, existing.filter((tp) => tp.player_id !== displaced.player_id));
            return next;
          });
        }

        // Step 1: Add player to team roster
        const rosterRes = await api.addTeamPlayer(teamId, playerId);
        setRosters((prev) => {
          const next = new Map(prev);
          const existing = next.get(teamId) ?? [];
          next.set(teamId, [...existing, rosterRes.player]);
          return next;
        });

        // Step 2: Assign position
        const posRes = await api.setPlayerPosition(teamId, playerId, {
          jersey_number: jerseyNumber,
          is_captain: false,
          is_vice_captain: false,
          secondary_positions: [],
        });
        setPositions((prev) => new Map(prev).set(teamId, posRes.positions));

        setSelectedPlayerId(null);
      } catch (e) {
        setError(e instanceof api.ApiError ? e.message : "Failed to assign player");
      } finally {
        setSaving(false);
      }
    },
    [activeTeamId, saving, positions],
  );

  // Handle click on a position card
  const handlePositionClick = useCallback(
    async (jerseyNumber: number) => {
      if (!activeTeamId || saving) return;

      // If a pool player is selected, assign them to this position
      if (selectedPlayerId && isPlayerAvailableForActiveTeam(selectedPlayerId)) {
        await assignPlayerToPosition(selectedPlayerId, jerseyNumber);
        return;
      }

      // If clicking an occupied position, select/highlight that player
      const holder = activePositions.find((p) => p.jersey_number === jerseyNumber);
      if (holder) {
        // Toggle: clicking same player deselects
        setHighlightedPlayerId((prev) =>
          prev === holder.player_id ? null : holder.player_id,
        );
        // Clear pool selection since we're focusing a roster player
        setSelectedPlayerId(null);
      }
    },
    [activeTeamId, selectedPlayerId, activePositions, saving, assignPlayerToPosition],
  );

  // Handle drag-drop onto a position card
  const handlePositionDrop = useCallback(
    async (jerseyNumber: number, playerId: string, fromPosition?: number) => {
      if (!activeTeamId || saving) return;

      // If player is already on this team (reposition within formation)
      const existingEntry = activePositions.find((p) => p.player_id === playerId);
      if (existingEntry) {
        // Don't do anything if dropping on same position
        if (existingEntry.jersey_number === jerseyNumber) return;

        setSaving(true);
        setError(null);
        try {
          // Check if the target position is occupied -- if so, swap
          const targetHolder = activePositions.find((p) => p.jersey_number === jerseyNumber);
          if (targetHolder) {
            // Swap: move target player to the source position first
            // Clear secondary positions if moving to a starter slot (only reserves can have them)
            const targetGoesToStarter = activeSportPositions ? !isReservePosition(activeSportPositions, existingEntry.jersey_number) : false;
            await api.setPlayerPosition(activeTeamId, targetHolder.player_id, {
              jersey_number: existingEntry.jersey_number,
              is_captain: targetHolder.is_captain,
              is_vice_captain: targetHolder.is_vice_captain,
              secondary_positions: targetGoesToStarter ? [] : targetHolder.covers_positions,
            });
          }
          // Move the dragged player to the target position
          // Clear secondary positions if moving to a starter slot
          const draggedGoesToStarter = activeSportPositions ? !isReservePosition(activeSportPositions, jerseyNumber) : false;
          const res = await api.setPlayerPosition(activeTeamId, playerId, {
            jersey_number: jerseyNumber,
            is_captain: existingEntry.is_captain,
            is_vice_captain: existingEntry.is_vice_captain,
            secondary_positions: draggedGoesToStarter ? [] : existingEntry.covers_positions,
          });
          setPositions((prev) => new Map(prev).set(activeTeamId, res.positions));
          setHighlightedPlayerId(null);
        } catch (e) {
          setError(e instanceof api.ApiError ? e.message : "Failed to move player");
        } finally {
          setSaving(false);
        }
        return;
      }

      // New player from pool -- assignPlayerToPosition handles displacing the existing holder
      if (!isPlayerAvailableForActiveTeam(playerId)) return;
      await assignPlayerToPosition(playerId, jerseyNumber);
    },
    [activeTeamId, activePositions, saving, assignPlayerToPosition],
  );

  // Remove player from team (position + roster)
  const handleRemovePlayer = useCallback(
    async (playerId: string) => {
      if (!activeTeamId) return;
      setSaving(true);
      setError(null);
      try {
        // Remove position first (if exists)
        const hasPosition = activePositions.some((p) => p.player_id === playerId);
        if (hasPosition) {
          await api.deletePlayerPosition(activeTeamId, playerId);
        }
        // Remove from roster
        await api.removeTeamPlayer(activeTeamId, playerId);

        setPositions((prev) => {
          const next = new Map(prev);
          const existing = next.get(activeTeamId) ?? [];
          next.set(activeTeamId, existing.filter((p) => p.player_id !== playerId));
          return next;
        });
        setRosters((prev) => {
          const next = new Map(prev);
          const existing = next.get(activeTeamId) ?? [];
          next.set(activeTeamId, existing.filter((tp) => tp.player_id !== playerId));
          return next;
        });
      } catch (e) {
        setError(e instanceof api.ApiError ? e.message : "Failed to remove player");
      } finally {
        setSaving(false);
      }
    },
    [activeTeamId, activePositions],
  );

  const handleCreateTeam = useCallback(
    async (isTrial: boolean) => {
      setSaving(true);
      try {
        const res = await api.createTeam(schoolId, {
          age_group_id: ageGroup.id,
          sport_id: ageGroup.sport_id,
          age_group: ageGroup.name,
          team_type: isTrial ? "trial" : "main",
        });
        setTeams((prev) => [...prev, res.team]);
        setRosters((prev) => new Map(prev).set(res.team.id, []));
        setPositions((prev) => new Map(prev).set(res.team.id, []));
        setActiveTeamId(res.team.id);
      } catch (e) {
        setError(e instanceof api.ApiError ? e.message : "Failed to create team");
      } finally {
        setSaving(false);
      }
    },
    [schoolId, ageGroup],
  );

  const handleDeleteTeam = useCallback(
    async (teamId: string) => {
      setSaving(true);
      try {
        await api.deleteTeam(teamId);
        setTeams((prev) => prev.filter((t) => t.id !== teamId));
        setRosters((prev) => { const n = new Map(prev); n.delete(teamId); return n; });
        setPositions((prev) => { const n = new Map(prev); n.delete(teamId); return n; });
        if (activeTeamId === teamId) {
          const remaining = teams.filter((t) => t.id !== teamId);
          setActiveTeamId(remaining[0]?.id ?? null);
        }
      } catch (e) {
        setError(e instanceof api.ApiError ? e.message : "Failed to delete team");
      } finally {
        setSaving(false);
      }
    },
    [activeTeamId, teams],
  );

  // Convert trial team to main (037)
  const handleConvertTeam = useCallback(
    async (teamId: string) => {
      setSaving(true);
      setError(null);
      try {
        const res = await api.convertTrialTeam(teamId);
        setTeams((prev) => prev.map((t) => (t.id === teamId ? res.team : t)));
      } catch (e) {
        setError(e instanceof api.ApiError ? e.message : "Failed to convert team");
      } finally {
        setSaving(false);
      }
    },
    [],
  );

  // Toggle captain status for a player
  const handleToggleCaptain = useCallback(
    async (playerId: string) => {
      if (!activeTeamId || saving) return;
      const entry = activePositions.find((p) => p.player_id === playerId);
      if (!entry) return;

      setSaving(true);
      setError(null);
      try {
        const res = await api.setPlayerPosition(activeTeamId, playerId, {
          jersey_number: entry.jersey_number,
          is_captain: !entry.is_captain,
          is_vice_captain: entry.is_captain ? entry.is_vice_captain : false,
          secondary_positions: entry.covers_positions,
        });
        setPositions((prev) => new Map(prev).set(activeTeamId, res.positions));
      } catch (e) {
        setError(e instanceof api.ApiError ? e.message : "Failed to update captain");
      } finally {
        setSaving(false);
      }
    },
    [activeTeamId, activePositions, saving],
  );

  // Toggle vice-captain status for a player
  const handleToggleVC = useCallback(
    async (playerId: string) => {
      if (!activeTeamId || saving) return;
      const entry = activePositions.find((p) => p.player_id === playerId);
      if (!entry) return;

      setSaving(true);
      setError(null);
      try {
        const res = await api.setPlayerPosition(activeTeamId, playerId, {
          jersey_number: entry.jersey_number,
          is_captain: entry.is_vice_captain ? entry.is_captain : false,
          is_vice_captain: !entry.is_vice_captain,
          secondary_positions: entry.covers_positions,
        });
        setPositions((prev) => new Map(prev).set(activeTeamId, res.positions));
      } catch (e) {
        setError(e instanceof api.ApiError ? e.message : "Failed to update vice-captain");
      } finally {
        setSaving(false);
      }
    },
    [activeTeamId, activePositions, saving],
  );

  // Update standing player role (captain/VC) via TeamPlayer.role (037)
  const handleUpdatePlayerRole = useCallback(
    async (playerId: string, role: TeamPlayerRole) => {
      if (!activeTeamId || saving) return;
      setSaving(true);
      setError(null);
      try {
        const res = await api.updatePlayerRole(activeTeamId, playerId, role);
        setRosters((prev) => {
          const next = new Map(prev);
          const existing = next.get(activeTeamId) ?? [];
          next.set(activeTeamId, existing.map((tp) =>
            tp.player_id === playerId ? res.player : tp,
          ));
          return next;
        });
      } catch (e) {
        setError(e instanceof api.ApiError ? e.message : "Failed to update player role");
      } finally {
        setSaving(false);
      }
    },
    [activeTeamId, saving],
  );

  // Bulk assign selected players (037)
  const handleBulkAssign = useCallback(
    async () => {
      if (!activeTeamId || saving || selectedPlayerIds.size === 0) return;
      setSaving(true);
      setError(null);
      try {
        const ids = [...selectedPlayerIds];
        const res = await api.bulkAssignPlayers(activeTeamId, ids);

        // Refresh roster for this team
        const rosterRes = await api.getTeamPlayers(activeTeamId);
        setRosters((prev) => new Map(prev).set(activeTeamId, rosterRes.players));

        setSelectedPlayerIds(new Set());
        if (res.skipped > 0) {
          setError(`Assigned ${res.assigned}, skipped ${res.skipped} (already on team or ineligible)`);
        }
      } catch (e) {
        setError(e instanceof api.ApiError ? e.message : "Failed to bulk assign players");
      } finally {
        setSaving(false);
      }
    },
    [activeTeamId, saving, selectedPlayerIds],
  );

  const handleToggleMultiSelect = useCallback(
    (playerId: string) => {
      setSelectedPlayerIds((prev) => {
        const next = new Set(prev);
        if (next.has(playerId)) {
          next.delete(playerId);
        } else {
          next.add(playerId);
        }
        return next;
      });
    },
    [],
  );

  const handleAssignCoach = useCallback(
    async (coachId: string) => {
      if (!activeTeamId || saving) return;
      setSaving(true);
      setError(null);
      try {
        const res = await api.assignCoachToTeam(activeTeamId, coachId);
        setCoaches((prev) => {
          const next = new Map(prev);
          const existing = next.get(activeTeamId) ?? [];
          next.set(activeTeamId, [...existing, res.coach]);
          return next;
        });
      } catch (e) {
        setError(e instanceof api.ApiError ? e.message : "Failed to assign coach");
      } finally {
        setSaving(false);
      }
    },
    [activeTeamId, saving],
  );

  const handleUnassignCoach = useCallback(
    async (coachId: string) => {
      if (!activeTeamId || saving) return;
      setSaving(true);
      setError(null);
      try {
        await api.unassignCoachFromTeam(activeTeamId, coachId);
        setCoaches((prev) => {
          const next = new Map(prev);
          const existing = next.get(activeTeamId) ?? [];
          next.set(activeTeamId, existing.filter((c) => c.coach_id !== coachId));
          return next;
        });
      } catch (e) {
        setError(e instanceof api.ApiError ? e.message : "Failed to unassign coach");
      } finally {
        setSaving(false);
      }
    },
    [activeTeamId, saving],
  );

  const handleDropOnTab = useCallback(
    (teamId: string, playerId: string) => {
      // Switch to that team tab -- user will then place on formation
      setActiveTeamId(teamId);
      setSelectedPlayerId(playerId);
    },
    [],
  );

  // Update reserve coverage (secondary positions) for a player
  const handleUpdateCoverage = useCallback(
    async (playerId: string, secondaryPositions: number[]) => {
      if (!activeTeamId || saving) return;
      const entry = activePositions.find((p) => p.player_id === playerId);
      if (!entry) return;

      setSaving(true);
      setError(null);
      try {
        const res = await api.setPlayerPosition(activeTeamId, playerId, {
          jersey_number: entry.jersey_number,
          is_captain: entry.is_captain,
          is_vice_captain: entry.is_vice_captain,
          secondary_positions: secondaryPositions,
        });
        setPositions((prev) => new Map(prev).set(activeTeamId, res.positions));
      } catch (e) {
        setError(e instanceof api.ApiError ? e.message : "Failed to update coverage");
      } finally {
        setSaving(false);
      }
    },
    [activeTeamId, activePositions, saving],
  );

  // Assign a pool player as a reserve covering a specific starter position
  const handleCoverageAssignReserve = useCallback(
    async (playerId: string, coverPosition: number) => {
      if (!activeTeamId || saving) return;
      if (!isPlayerAvailableForActiveTeam(playerId)) return;
      if (!activeSportPositions) return;

      // Find first open reserve position
      const occupiedNumbers = new Set(activePositions.map((p) => p.jersey_number));
      const openReserve = activeSportPositions.positions.find(
        (p) => p.group === "reserves" && !occupiedNumbers.has(p.number),
      );
      if (!openReserve) {
        setError("All reserve positions are filled");
        return;
      }

      // Step 1: Assign to roster + reserve position
      await assignPlayerToPosition(playerId, openReserve.number);

      // Step 2: Set coverage -- use the known reserve jersey number directly
      // (can't read updated state yet since React batches updates)
      setSaving(true);
      try {
        const res = await api.setPlayerPosition(activeTeamId, playerId, {
          jersey_number: openReserve.number,
          is_captain: false,
          is_vice_captain: false,
          secondary_positions: [coverPosition],
        });
        setPositions((prev) => new Map(prev).set(activeTeamId, res.positions));
      } catch {
        // Assignment succeeded but coverage failed -- not critical
      } finally {
        setSaving(false);
      }
    },
    [activeTeamId, saving, activePositions, activeSportPositions, assignPlayerToPosition],
  );

  // --- 184: dnd-kit drag-and-drop (works on touch via Mouse+Touch sensors) ---
  // Desktop: drag after an 8px move; a plain tap still fires an element's onClick
  // (select / multi-select / captain / remove). Touch: long-press to drag while a
  // swipe still scrolls the pool. `pointerWithin` resolves the drop target by the
  // pointer position, and all drop routing is centralised in handleDragEnd.
  const sensors = useDndSensors();
  const [activeDragLabel, setActiveDragLabel] = useState<string | null>(null);

  function handleDragStart(event: DragStartEvent) {
    const label = event.active.data.current?.label;
    setActiveDragLabel(typeof label === "string" ? label : null);
  }

  async function handleTabReorder(movedTeamId: string, targetTeamId: string, teamType: "main" | "trial") {
    if (movedTeamId === targetTeamId) return;
    const partition = teams.filter((t) => t.team_type === teamType).sort((a, b) => a.rank - b.rank);
    const currentIds = partition.map((t) => t.id);
    const fromIdx = currentIds.indexOf(movedTeamId);
    const toIdx = currentIds.indexOf(targetTeamId);
    if (fromIdx < 0 || toIdx < 0) return;
    const nextIds = [...currentIds];
    nextIds.splice(fromIdx, 1);
    nextIds.splice(toIdx, 0, movedTeamId);
    try {
      const res = await api.reorderTeams(schoolId, ageGroup.id, nextIds);
      setTeams(res.teams);
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to reorder teams");
    }
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveDragLabel(null);
    const a = event.active.data.current as
      | { kind: string; playerId?: string; fromPosition?: number; teamId?: string; teamType?: "main" | "trial" }
      | undefined;
    const o = event.over?.data.current as
      | { kind: string; jersey?: number; posNumber?: number; teamId?: string; teamType?: "main" | "trial" }
      | undefined;
    if (!a || !o) return;

    // Assign / move / swap a player into a formation position.
    if ((a.kind === "pool-player" || a.kind === "position-player") && o.kind === "position" && a.playerId && o.jersey != null) {
      handlePositionDrop(o.jersey, a.playerId, a.kind === "position-player" ? a.fromPosition : undefined);
      return;
    }
    // Drag an assigned player back to the pool -> remove from team.
    if (a.kind === "position-player" && o.kind === "pool" && a.playerId) {
      handleRemovePlayer(a.playerId);
      return;
    }
    // Drop a player on a team tab -> switch to that team + select the player.
    if ((a.kind === "pool-player" || a.kind === "position-player") && o.kind === "team-tab" && a.playerId && o.teamId) {
      handleDropOnTab(o.teamId, a.playerId);
      return;
    }
    // Reorder team tabs within a partition.
    if (a.kind === "team-tab" && o.kind === "team-tab" && a.teamId && o.teamId && a.teamType && o.teamType === a.teamType) {
      void handleTabReorder(a.teamId, o.teamId, a.teamType);
      return;
    }
    // Coverage view: assign a pool player as a reserve covering a starter.
    if (a.kind === "pool-player" && o.kind === "coverage-starter" && a.playerId && o.posNumber != null) {
      handleCoverageAssignReserve(a.playerId, o.posNumber);
      return;
    }
    // Coverage view: toggle an existing reserve's coverage of a starter.
    if (a.kind === "coverage-player" && o.kind === "coverage-starter" && a.playerId && o.posNumber != null) {
      const reserve = activePositions.find((p) => p.player_id === a.playerId);
      if (!reserve) return;
      const covers = reserve.covers_positions.includes(o.posNumber)
        ? reserve.covers_positions.filter((n) => n !== o.posNumber)
        : [...reserve.covers_positions, o.posNumber];
      handleUpdateCoverage(a.playerId, covers);
      return;
    }
  }

  if (loading) {
    return (
      <div className="px-4 py-6 text-sm text-muted-foreground">Loading team builder...</div>
    );
  }

  return (
    <div className="border-t border-border bg-muted/10 p-4">
      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
      {error && (
        <div className="mb-3 flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2">
          <p className="text-sm text-destructive flex-1">{error}</p>
          <button
            type="button"
            onClick={() => setError(null)}
            className="text-xs text-muted-foreground underline shrink-0"
          >
            Dismiss
          </button>
        </div>
      )}
      {/* Team tab bar */}
      <div className="mb-4">
        <TeamTabBar
          teams={teams}
          activeTeamId={activeTeamId}
          onSelectTeam={setActiveTeamId}
          onDeleteTeam={handleDeleteTeam}
          onCreateTeam={handleCreateTeam}
          onConvertTeam={handleConvertTeam}
          canEdit={canEdit}
          saving={saving}
        />
      </div>

      {/* 184: pool | formation+roster -- side-by-side on lg+, stacked (pool above
          the field) below lg so it fits a phone/tablet. Core lineup-building is
          tap-to-assign (select a pool player, tap a position); drag is desktop. */}
      <div className="flex flex-col gap-4 lg:flex-row">
        <PlayerPoolSidebar
          pool={pool}
          assignedMap={assignedMap}
          isPlayerAvailable={isPlayerAvailableForActiveTeam}
          activeTeamName={activeTeam?.name}
          activeTeamId={activeTeamId ?? undefined}
          sportPositions={activeSportPositions}
          selectedPlayerId={selectedPlayerId}
          selectedPlayerIds={selectedPlayerIds}
          saving={saving}
          onSelectPlayer={(id) => {
            setSelectedPlayerId(id);
            if (id) setHighlightedPlayerId(null);
          }}
          onToggleMultiSelect={handleToggleMultiSelect}
          onBulkAssign={handleBulkAssign}
          onClearSelection={() => setSelectedPlayerIds(new Set())}
        />

        {activeTeam ? (
          <TeamRosterPanel
            team={activeTeam}
            roster={activeRoster}
            positionData={activePositions}
            coaches={activeCoaches}
            schoolCoaches={schoolCoaches}
            loading={rosterLoading}
            positionsLoading={positionsLoading}
            saving={saving}
            selectedPlayerId={selectedPlayerId}
            highlightedPlayerId={highlightedPlayerId}
            onPositionClick={handlePositionClick}
            onRemovePlayer={handleRemovePlayer}
            onToggleCaptain={handleToggleCaptain}
            onToggleVC={handleToggleVC}
            onAssignCoach={handleAssignCoach}
            onUnassignCoach={handleUnassignCoach}
            onUpdatePlayerRole={handleUpdatePlayerRole}
            onUpdateCoverage={handleUpdateCoverage}
          />
        ) : (
          <div className="flex-1 flex items-center justify-center py-8">
            <p className="text-sm text-muted-foreground">
              {teams.length === 0
                ? "No teams yet. Create one to get started."
                : "Select a team"}
            </p>
          </div>
        )}
      </div>
        <DragOverlay>
          {activeDragLabel ? (
            <div className="rounded-md border border-primary bg-background px-2 py-1 text-xs font-medium shadow-lg">
              {activeDragLabel}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
}
